/**
* Hook to manage all chat room data: messages subscription, pause status, and stop AI
* Note: Chatroom creation is now handled lazily on first message/interaction (not on initialization)
*/

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import type { IMessage } from '../../types'
import type { WidgetMessage } from '../types'

export interface UseChatRoomDataReturn {
    isPaused: boolean
    displayedMessages: WidgetMessage[]
    handleStopAI: () => Promise<void>
}

export function useChatRoomData(
    firestore: Firestore | null,
    chatRoomId: string,
    clientId: string,
    visitorId: string,
    visitorName: string,
    apiKey: string,
    baseGreeting: WidgetMessage,
    setIsTyping: (value: boolean) => void
): UseChatRoomDataReturn {
    const [isPaused, setIsPaused] = useState(false)
    const [remoteMessages, setRemoteMessages] = useState<WidgetMessage[]>([])
    const lastMessageCountRef = useRef(0)

    // Subscribe to chat room document for pauseAI status
    useEffect(() => {
        if (!clientId || !visitorId || !firestore) {
            return
        }

        const chatRoomRef = doc(firestore, 'widgetChatRooms', chatRoomId)

        const unsubscribe = onSnapshot(
            chatRoomRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data()
                    const pauseAI = data?.pauseAI === true
                    setIsPaused(pauseAI)

                    // If AI is paused, immediately hide typing indicator
                    if (pauseAI) {
                        setIsTyping(false)
                    }
                }
            },
            (error) => {
                console.error('[Chat Widget] Failed to subscribe to chat room', error)
            },
        )

        return () => unsubscribe()
    }, [chatRoomId, clientId, visitorId, firestore, setIsTyping])

    // Subscribe to messages
    useEffect(() => {
        if (!clientId || !visitorId || !firestore) {
            return
        }

        const messagesRef = collection(firestore, 'widgetChatRooms', chatRoomId, 'messages')
        const messagesQuery = query(messagesRef, orderBy('date', 'asc'))

        const unsubscribe = onSnapshot(
            messagesQuery,
            (snapshot) => {
                const nextMessages: WidgetMessage[] = snapshot.docs.map((docSnapshot) => {
                    const data = docSnapshot.data() as IMessage
                    // Messages from business side (AI or human agent) show as assistant
                    // fromAI = AI response, fromMe = human agent response
                    const isFromBusiness = data.fromAI || data.fromMe
                    return {
                        id: docSnapshot.id,
                        sender: isFromBusiness ? 'assistant' : 'user',
                        text: data.body ?? '',
                        type: data.type || 'chat',
                        downloadUrl: data.downloadUrl,
                        caption: data.caption,
                        mimeType: data.mimeType,
                        mediaFileName: data.mediaFileName || data.originalFileName || (data.downloadUrl ? data.downloadUrl.split('/').pop() : undefined),
                        interactivePayload: data.interactivePayload,
                    }
                })

                // Hide typing indicator when new AI/assistant message arrives
                const assistantMessages = nextMessages.filter(m => m.sender === 'assistant')
                if (assistantMessages.length > lastMessageCountRef.current) {
                    setIsTyping(false)
                }
                lastMessageCountRef.current = assistantMessages.length

                setRemoteMessages(nextMessages)
            },
            (error) => {
                console.error('[Chat Widget] Failed to subscribe to messages', error)
            },
        )

        return () => unsubscribe()
    }, [chatRoomId, clientId, visitorId, firestore, setIsTyping])

    // Stop AI handler
    const handleStopAI = useCallback(async () => {
        if (!clientId || !visitorId || !firestore || isPaused) {
            return
        }

        try {
            const chatRoomRef = doc(firestore, 'widgetChatRooms', chatRoomId)
            await updateDoc(chatRoomRef, {
                pauseAI: true,
            })
        } catch (error) {
            console.error('[Chat Widget] Failed to stop AI', error)
        }
    }, [chatRoomId, clientId, visitorId, firestore, isPaused])

    const displayedMessages = useMemo(
        () => [baseGreeting, ...remoteMessages],
        [baseGreeting, remoteMessages],
    )

    return {
        isPaused,
        displayedMessages,
        handleStopAI,
    }
}
